import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SearchBuilder, { applyFilters } from '@/components/search/SearchBuilder'
import useTableColumns from '@/hooks/useTableColumns';
import UrlFilterChip from '@/components/UrlFilterChip';
import useListKeyboardNav from '@/hooks/useListKeyboardNav';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';
import { formatScheduleLabel, formatTime, lessonFormForHeadcount, WEEKDAYS_SHORT, WEEKDAYS_LONG } from '@/lib/utils';
import { courseTypeForHeadcount } from '@/lib/deLabels';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';

const COURSE_SORT_ACCESSORS = {
  name: (c) => c.name,
  subject_name: (c) => c.subject_name,
  teacher_name: (c) => c.teacher_name,
  weekday_label: (c) => (Number.isInteger(c.day_of_week) ? c.day_of_week : 99),
  time_label: (c) => c.start_time || '',
  location_name: (c) => c.location_name,
  room_name: (c) => c.room_name,
  course_type: (c) => c.course_type,
  enrollment_count: (c) => Number(c.enrollment_count || 0),
  status: (c) => c.status,
};

const COURSE_TYPE_SEARCH = {
  individual: 'Einzelunterricht Einzel',
  group: 'Gruppenunterricht Gruppe',
  workshop: 'Workshop',
};

const FILTER_FIELDS = [
  { key: 'name', label: 'Kursname', type: 'text' },
  { key: 'teacher_name', label: 'Lehrer', type: 'text' },
  { key: 'subject_name', label: 'Fach', type: 'text' },
  { key: 'weekday_label', label: 'Wochentag', type: 'text' },
  { key: 'start_time', label: 'Uhrzeit', type: 'time' },
  { key: 'location_name', label: 'Standort', type: 'text' },
  { key: 'room_name', label: 'Raum', type: 'text' },
  { key: 'course_type', label: 'Typ', type: 'select', options: [
    { value: 'individual', label: 'Einzelunterricht' },
    { value: 'group', label: 'Gruppenunterricht' },
    { value: 'workshop', label: 'Workshop' },
  ]},
  { key: 'enrollment_count', label: 'Teilnehmer', type: 'number' },
];

const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Kursname', visible: true },
  { key: 'subject_name', label: 'Fach', visible: true },
  { key: 'teacher_name', label: 'Lehrer', visible: true },
  { key: 'weekday_label', label: 'Tag', visible: true },
  { key: 'time_label', label: 'Uhrzeit', visible: true },
  { key: 'location_name', label: 'Standort', visible: true },
  { key: 'room_name', label: 'Raum', visible: true },
  { key: 'course_type', label: 'Typ', visible: true },
  { key: 'enrollment_count', label: 'Teilnehmer', visible: true },
  { key: 'status', label: 'Status', visible: true },
];

export function courseListStatus(course, today = new Date()) {
  const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const start = course?.start_date ? String(course.start_date).slice(0, 10) : '';
  const end = course?.end_date ? String(course.end_date).slice(0, 10) : '';
  if (start && start > day) return 'future';
  if (end && end < day) return 'expired';
  if (start || end) return course.status === 'full' ? 'full' : 'active';
  if (course?.status === 'inactive') return 'inactive';
  if (course?.status === 'full') return 'full';
  return 'active';
}

const STATUS_LABEL = {
  active: 'Aktiv',
  future: 'Zukünftig',
  expired: 'Inaktiv',
  inactive: 'Inaktiv',
  full: 'Ausgebucht',
};

export function formatCourseTime(course) {
  const start = formatTime(course?.start_time);
  if (!start || !/^\d{2}:\d{2}$/.test(start)) return start;
  const mins = Number(course.duration_minutes);
  if (!mins) return start;
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + mins;
  const end = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  return `${start}–${end}`;
}

function enrichCourseForSearch(course) {
  const dayIdx = course.day_of_week;
  const weekdayLabel = Number.isInteger(dayIdx) ? (WEEKDAYS_LONG[dayIdx] || '') : '';
  const dayBits = Number.isInteger(dayIdx)
    ? [WEEKDAYS_SHORT[dayIdx], weekdayLabel].filter(Boolean).join(' ')
    : '';
  const start = formatTime(course.start_time);
  const teacher = course.teacher_name || '';
  const teacherReversed = teacher.includes(' ')
    ? `${teacher.split(' ').slice(-1)[0]}, ${teacher.split(' ').slice(0, -1).join(' ')}`
    : '';
  return {
    ...course,
    start_time: start || course.start_time,
    weekday_label: weekdayLabel,
    time_label: formatCourseTime({ ...course, start_time: start }),
    _search: [
      course.name,
      teacher,
      teacherReversed,
      course.subject_name,
      course.room_name,
      course.location_name,
      course.location_search_term,
      course.location_short_code,
      course.location_city,
      COURSE_TYPE_SEARCH[course.course_type] || course.course_type,
      dayBits,
      course.start_time,
      course.start_date,
      course.end_date,
      STATUS_LABEL[courseListStatus(course)],
      course.status,
    ].filter(Boolean).join(' '),
  };
}

/**
 * @param {'active' | 'inactive'} mode
 */
const PERIOD_META = {
  active: { title: 'Aktive Kurse', empty: 'Keine aktiven Kurse gefunden', testId: 'courses-active-page' },
  future: { title: 'Zukünftige Kurse', empty: 'Keine zukünftigen Kurse gefunden', testId: 'courses-future-page' },
  expired: { title: 'Inaktive Kurse', empty: 'Keine inaktiven Kurse gefunden', testId: 'courses-expired-page' },
  all: { title: 'Alle Kurse', empty: 'Keine Kurse gefunden', testId: 'courses-all-page' },
  inactive: { title: 'Inaktive Kurse', empty: 'Keine inaktiven Kurse gefunden', testId: 'courses-inactive-page' },
};

export default function CoursesListView({ mode }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [footerTick, setFooterTick] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const { columns, setColumns } = useTableColumns('courses-list', DEFAULT_COLUMNS);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const period = mode === 'inactive' ? 'inactive' : (searchParams.get('period') || 'active');
  const periodMeta = PERIOD_META[period] || PERIOD_META.active;
  const { user } = useAuth();
  const { isSuperAdmin } = useTenant();
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacher = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !isSuperAdmin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [mode]);

  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  useEffect(() => {
    const initialFilters = [];
    if (searchParams.get('has_enrollment') === 'true') {
      initialFilters.push({
        id: 'auto_he',
        field: 'enrollment_count',
        fieldType: 'number',
        operator: 'greater',
        value: 0,
      });
    }
    if (initialFilters.length) setFilters(initialFilters);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        let allData = [];
        let offset = 0;
        const batchSize = 100;
        while (true) {
          const params = new URLSearchParams({ limit: String(batchSize), offset: String(offset) });
          Object.entries(footerToParams(getFooterSettings())).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
          });
          if (period === 'inactive') params.set('status', 'inactive');
          else params.set('period', period);
          const response = await api.get(`/courses?${params.toString()}`);
          const batch = response.data;
          if (!batch || batch.length === 0) break;
          allData = [...allData, ...batch];
          if (batch.length < batchSize) break;
          offset += batchSize;
        }
        if (!cancelled) setCourses(allData.map(enrichCourseForSearch));
      } catch (error) {
        console.error('Error loading courses:', error);
        toast.error('Fehler beim Laden der Kurse');
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [period, footerTick]);

  const { sort, toggleSort, sortRows } = useTableSort(COURSE_SORT_ACCESSORS, { key: 'name' });

  const filteredCourses = useMemo(() => {
    // Ein Suchfeld-Blob: Mehrwort-Suche (AND) über Name, Fach, Lehrer, Standort, Raum, Tag, Typ, …
    return sortRows(applyFilters(courses, filters, searchTerm, ['_search']));
  }, [courses, filters, searchTerm, sortRows]);

  const filteredParticipantSum = useMemo(
    () => filteredCourses.reduce((n, c) => n + (Number(c.enrollment_count) || 0), 0),
    [filteredCourses],
  );

  // Erzwingt neues tbody bei Filterwechsel (verhindert „Geisterzeilen“ aus vorheriger Liste)
  const tableBodyKey = useMemo(
    () => `${searchTerm}|${filters.map((f) => `${f.id}:${f.value}`).join(',')}|${filteredCourses.map((c) => c.id).join(',')}`,
    [searchTerm, filters, filteredCourses],
  );

  const highlightMatch = (text) => {
    const raw = String(text || '');
    if (!raw || !searchTerm.trim()) return raw || '—';
    const tokens = searchTerm
      .toLowerCase()
      .replace(/[|]+/g, ' ')
      .split(/\s+/)
      .map((t) => t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').trim())
      .filter(Boolean);
    if (!tokens.length) return raw;
    const lower = raw.toLowerCase();
    const hit = tokens.some((t) => lower.includes(t));
    if (!hit) return raw;
    return <span className="font-semibold text-brand-red">{raw}</span>;
  };

  const { getRowProps } = useListKeyboardNav({
    items: filteredCourses,
    onSelect: (c) => navigate(`/courses/${c.id}`),
  });

  const getStatusBadge = (course) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      future: { label: 'Zukünftig', className: 'bg-sky-50 text-sky-800 border-sky-200' },
      expired: { label: 'Inaktiv', className: 'bg-slate-100 text-slate-700' },
      inactive: { label: 'Inaktiv', className: 'bg-slate-100 text-slate-700' },
      full: { label: 'Ausgebucht', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const config = variants[courseListStatus(course)] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getCourseTypeBadge = (course) => courseTypeForHeadcount(course.course_type, course.enrollment_count);

  const isColumnVisible = (key) => {
    const col = columns.find((c) => c.key === key);
    return col ? col.visible : true;
  };

  const isHitMode = Boolean(searchTerm.trim() || filters.length);

  const renderCourseCells = (course) => (
    <>
      {isColumnVisible('name') && (
        <td className="p-4 text-sm text-slate-700 font-medium">
          {lessonFormForHeadcount(formatScheduleLabel(course.name), course.enrollment_count)}
        </td>
      )}
      {isColumnVisible('subject_name') && (
        <td className="p-4 text-sm text-slate-600">{course.subject_name || '-'}</td>
      )}
      {isColumnVisible('teacher_name') && (
        <td className="p-4 text-sm text-slate-600">{course.teacher_name || '-'}</td>
      )}
      {isColumnVisible('weekday_label') && (
        <td className="p-4 text-sm text-slate-600" data-testid={`course-day-${course.id}`}>{course.weekday_label || '—'}</td>
      )}
      {isColumnVisible('time_label') && (
        <td className="p-4 text-sm text-slate-700 font-mono" data-testid={`course-time-${course.id}`}>{course.time_label || '—'}</td>
      )}
      {isColumnVisible('location_name') && (
        <td className="p-4 text-sm text-slate-600">{highlightMatch(course.location_name)}</td>
      )}
      {isColumnVisible('room_name') && (
        <td className="p-4 text-sm text-slate-600">{highlightMatch(course.room_name)}</td>
      )}
      {isColumnVisible('course_type') && (
        <td className="p-4 text-sm text-slate-600">
          {getCourseTypeBadge(course)}
        </td>
      )}
      {isColumnVisible('enrollment_count') && (
        <td className="p-4 text-sm">
          <span className="font-mono text-xs font-semibold">{course.enrollment_count}</span>
        </td>
      )}
      {isColumnVisible('status') && (
        <td className="p-4 text-sm">{getStatusBadge(course)}</td>
      )}
    </>
  );

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid={periodMeta.testId}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1
              className="text-3xl font-bold tracking-tight text-slate-900 mb-1"
              data-testid="courses-title"
            >
              {periodMeta.title}
            </h1>
            <p className="text-slate-600">
              {filteredCourses.length} von {courses.length} Kursen
              {searchTerm.trim() && filteredParticipantSum > 0 ? (
                <span className="text-slate-400"> · {filteredParticipantSum} Teilnehmer in den Treffern</span>
              ) : null}
            </p>
            <UrlFilterChip
              params={[
                { key: 'has_enrollment', label: 'Belegung', valueLabels: { true: 'mit Teilnehmer' } },
              ]}
              onReset={() => setFilters([])}
            />
          </div>
          <div className="flex items-center gap-2">
            <div
              className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50"
              role="group"
              aria-label="Kursliste wechseln"
            >
              {[
                { id: 'active', label: 'Aktiv', to: '/courses' },
                { id: 'future', label: 'Zukünftig', to: '/courses?period=future' },
                { id: 'expired', label: 'Inaktiv', to: '/courses?period=expired' },
                { id: 'all', label: 'Alle', to: '/courses?period=all' },
              ].map((chip) => (
                <Link
                  key={chip.id}
                  to={chip.to}
                  data-testid={`courses-filter-${chip.id}`}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    period === chip.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {chip.label}
                </Link>
              ))}
            </div>
            {period === 'active' && !isTeacher && (
              <Button
                onClick={() => toast.info('Funktion folgt in Phase 2')}
                data-testid="add-course-button"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neuer Kurs
              </Button>
            )}
          </div>
        </div>

        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={setSearchTerm}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Suche: Fach, Lehrer, Standort, Raum, Tag, Uhrzeit…"
          className="mb-4"
        />

        <div
          key={tableBodyKey}
          className="bg-white rounded-xl border border-slate-200 shadow-sm"
          data-testid="courses-results"
          data-hit-mode={isHitMode ? '1' : '0'}
          data-row-count={filteredCourses.length}
        >
          <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-slate-600" data-testid="courses-result-summary">
              {filteredCourses.length} {filteredCourses.length === 1 ? 'Kurs' : 'Kurse'}
              {searchTerm.trim() ? (
                <span className="text-slate-400"> für „{searchTerm.trim().replace(/\|/g, '').trim()}“</span>
              ) : null}
              {searchTerm.trim() && filteredParticipantSum > 0 ? (
                <span className="text-slate-400"> · {filteredParticipantSum} Teilnehmer</span>
              ) : null}
            </span>
            {isHitMode && filteredCourses.length > 0 && (
              <span className="text-xs text-brand-red font-medium">
                Nur Treffer — {filteredCourses.length === 1 ? 'kein weiterer Kurs' : 'keine weiteren Kurse'} darunter
              </span>
            )}
          </div>

          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red" />
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="courses-empty">
              {isHitMode
                ? 'Keine Kurse zu dieser Suche'
                : periodMeta.empty}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="courses-table">
                <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                      <SortableTh
                        key={col.key}
                        sortKey={col.key}
                        sort={sort}
                        onSort={toggleSort}
                        testIdPrefix="courses-sort"
                      >
                        {col.label}
                      </SortableTh>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="courses-table-body" data-row-count={filteredCourses.length}>
                  {filteredCourses.map((course, idx) => (
                    <tr
                      key={String(course.id)}
                      data-testid={`course-row-${course.id}`}
                      data-room={course.room_name || ''}
                      data-location={course.location_name || ''}
                      className="border-b border-slate-100 hover:bg-brand-red/5 transition-colors cursor-pointer data-[keyboard-selected=true]:bg-brand-red/10 data-[keyboard-selected=true]:ring-2 data-[keyboard-selected=true]:ring-brand-red/30 data-[keyboard-selected=true]:ring-inset"
                      {...getRowProps(idx, course)}
                    >
                      {renderCourseCells(course)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
